import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import TopMenu from '../../contexts/TopMenu';
import { adminApi } from '../../services/adminApi';
import '../../styles/BrandGallery.css';

const PAGE_SIZE = 72; // 12 rows of 6 items

function BrandGallery({ email, onLogout }) {
  const { brandId } = useParams();

  const [brand, setBrand] = useState(null);
  const [products, setProducts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [genders, setGenders] = useState([]);
  const [genderCounts, setGenderCounts] = useState({});
  const [activeGender, setActiveGender] = useState('all');
  const [activeStatus, setActiveStatus] = useState('all'); // 'all' | 'active' | 'disabled'
  const [statusCounts, setStatusCounts] = useState({ all: 0, active: 0, disabled: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState('');

  // Note editing state
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [noteSuccess, setNoteSuccess] = useState(false);

  // Modal Gallery state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  // Product status & gender toggle state & action toast
  const [togglingProductId, setTogglingProductId] = useState(null);
  const [updatingGenderProductId, setUpdatingGenderProductId] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);

  // Scroll sentinel ref for infinite scroll
  const sentinelRef = useRef(null);

  // Back to top button state
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Monitor scroll for Back to Top button
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchQuery.trim());
    }, 300);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Initial and filtered fetch
  const fetchInitialProducts = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.getBrandProducts(brandId, {
        gender: activeGender !== 'all' ? activeGender : undefined,
        status: activeStatus !== 'all' ? activeStatus : undefined,
        q: debouncedQuery || undefined,
        skip: 0,
        limit: PAGE_SIZE,
      });

      if (data) {
        setBrand(data.brand || null);
        const fetchedProducts = data.products || [];
        setProducts(fetchedProducts);
        const total = data.total ?? fetchedProducts.length;
        setTotalCount(total);
        setHasMore(fetchedProducts.length < total);

        if (data.genders && data.genders.length) {
          setGenders(data.genders);
        }
        if (data.gender_counts) {
          setGenderCounts(data.gender_counts);
        }
        if (data.status_counts) {
          setStatusCounts(data.status_counts);
        }

        const bNote = (
          data.brand?.notes ||
          data.brand?.note ||
          data.brand?.custom_fields?.notes ||
          data.brand?.custom_fields?.note ||
          ''
        ).trim();
        setNoteText(bNote);
      }
    } catch (err) {
      console.error('Failed to load brand products:', err);
      setError(err?.message || 'Failed to load brand products');
    } finally {
      setLoading(false);
    }
  }, [brandId, activeGender, activeStatus, debouncedQuery]);

  useEffect(() => {
    fetchInitialProducts();
  }, [fetchInitialProducts]);

  // Load next page on scroll
  const loadMoreProducts = useCallback(async () => {
    if (!brandId || loading || loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const currentSkip = products.length;
      const data = await adminApi.getBrandProducts(brandId, {
        gender: activeGender !== 'all' ? activeGender : undefined,
        status: activeStatus !== 'all' ? activeStatus : undefined,
        q: debouncedQuery || undefined,
        skip: currentSkip,
        limit: PAGE_SIZE,
      });

      if (data && data.products) {
        const newItems = data.products;
        setProducts((prev) => {
          const existingIds = new Set(prev.map((p) => p.id || p.handle));
          const filteredNew = newItems.filter((p) => !existingIds.has(p.id || p.handle));
          const combined = [...prev, ...filteredNew];
          const total = data.total ?? totalCount;
          setTotalCount(total);
          setHasMore(combined.length < total);
          return combined;
        });
      }
    } catch (err) {
      console.error('Failed to load more products:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [brandId, loading, loadingMore, hasMore, products.length, activeGender, activeStatus, debouncedQuery, totalCount]);

  // Setup IntersectionObserver for infinite scrolling
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || loading || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreProducts();
        }
      },
      {
        root: null,
        rootMargin: '500px', // Pre-fetch 500px before reaching the bottom
        threshold: 0.1,
      }
    );

    observer.observe(sentinel);
    return () => {
      observer.disconnect();
    };
  }, [hasMore, loading, loadingMore, loadMoreProducts]);

  // Preload top hero image in <head> for ultra-fast first contentful paint
  useEffect(() => {
    if (products.length > 0) {
      const topImgUrl = products[0]?.top_image || products[0]?.images?.[0]?.url;
      if (topImgUrl) {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = topImgUrl;
        document.head.appendChild(link);
        return () => {
          if (document.head.contains(link)) {
            document.head.removeChild(link);
          }
        };
      }
    }
  }, [products]);

  const handleGenderChange = (g) => {
    setActiveGender(g);
  };

  const handleStatusChange = (s) => {
    setActiveStatus(s);
  };

  const handleSaveNote = async () => {
    if (!brandId) return;
    setSavingNote(true);
    try {
      const updatedFields = {
        notes: noteText.trim(),
        custom_fields: {
          ...(brand?.custom_fields || {}),
          notes: noteText.trim(),
          note: noteText.trim(),
        },
      };
      await adminApi.updateBrand(brandId, updatedFields);
      setBrand((prev) => ({
        ...prev,
        notes: noteText.trim(),
        note: noteText.trim(),
        custom_fields: {
          ...(prev?.custom_fields || {}),
          notes: noteText.trim(),
          note: noteText.trim(),
        },
      }));
      setIsEditingNote(false);
      setNoteSuccess(true);
      setTimeout(() => setNoteSuccess(false), 2500);
    } catch (err) {
      alert(`Failed to save note: ${err?.message || 'Error saving'}`);
    } finally {
      setSavingNote(false);
    }
  };

  // Modal Gallery interactions
  const openModal = (product, initialIndex = 0) => {
    setSelectedProduct(product);
    setActiveImageIndex(initialIndex);
  };

  const closeModal = () => {
    setSelectedProduct(null);
    setActiveImageIndex(0);
  };

  const handleNextImage = useCallback(() => {
    if (!selectedProduct?.images?.length) return;
    setActiveImageIndex((prev) => (prev + 1) % selectedProduct.images.length);
  }, [selectedProduct]);

  const handlePrevImage = useCallback(() => {
    if (!selectedProduct?.images?.length) return;
    setActiveImageIndex((prev) => (prev - 1 + selectedProduct.images.length) % selectedProduct.images.length);
  }, [selectedProduct]);

  const currentProductIndex = selectedProduct
    ? products.findIndex((p) => (p.id && p.id === selectedProduct.id) || (p.handle && p.handle === selectedProduct.handle))
    : -1;

  const handlePrevProduct = useCallback(() => {
    if (currentProductIndex > 0) {
      setSelectedProduct(products[currentProductIndex - 1]);
      setActiveImageIndex(0);
    }
  }, [currentProductIndex, products]);

  const handleNextProduct = useCallback(async () => {
    if (currentProductIndex >= 0 && currentProductIndex < products.length - 1) {
      setSelectedProduct(products[currentProductIndex + 1]);
      setActiveImageIndex(0);
    } else if (hasMore && !loadingMore) {
      setLoadingMore(true);
      try {
        const currentSkip = products.length;
        const data = await adminApi.getBrandProducts(brandId, {
          gender: activeGender !== 'all' ? activeGender : undefined,
          status: activeStatus !== 'all' ? activeStatus : undefined,
          q: debouncedQuery || undefined,
          skip: currentSkip,
          limit: PAGE_SIZE,
        });
        if (data && data.products && data.products.length > 0) {
          const newItems = data.products;
          const existingIds = new Set(products.map((p) => p.id || p.handle));
          const filteredNew = newItems.filter((p) => !existingIds.has(p.id || p.handle));
          if (filteredNew.length > 0) {
            setProducts((prev) => [...prev, ...filteredNew]);
            setSelectedProduct(filteredNew[0]);
            setActiveImageIndex(0);
          }
          const total = data.total ?? totalCount;
          setTotalCount(total);
          setHasMore(products.length + filteredNew.length < total);
        }
      } catch (err) {
        console.error('Failed to load next product in modal:', err);
      } finally {
        setLoadingMore(false);
      }
    }
  }, [currentProductIndex, products, hasMore, loadingMore, brandId, activeGender, activeStatus, debouncedQuery, totalCount]);

  // Keyboard navigation for image modal and product switching
  useEffect(() => {
    if (!selectedProduct) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeModal();
      if (e.key === 'ArrowRight' && !e.shiftKey) handleNextImage();
      if (e.key === 'ArrowLeft' && !e.shiftKey) handlePrevImage();
      if ((e.key === 'ArrowRight' && e.shiftKey) || e.key === 'PageDown' || e.key === ']') handleNextProduct();
      if ((e.key === 'ArrowLeft' && e.shiftKey) || e.key === 'PageUp' || e.key === '[') handlePrevProduct();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedProduct, handleNextImage, handlePrevImage, handleNextProduct, handlePrevProduct]);

  const handleToggleProductStatus = async (product, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!product || !product.id) return;

    const isCurrentlyDiscontinued = Boolean(
      product.discontinued ||
      String(product.status || '').toLowerCase() === 'inactive' ||
      String(product.status || '').toLowerCase() === 'discontinued'
    );
    const targetDiscontinued = !isCurrentlyDiscontinued;
    const targetStatus = targetDiscontinued ? 'inactive' : 'active';

    setTogglingProductId(product.id);
    try {
      await adminApi.toggleProductStatus(product.id, {
        discontinued: targetDiscontinued,
        status: targetStatus,
      });

      // Update in products list
      setProducts((prev) =>
        prev.map((p) =>
          p.id === product.id
            ? {
                ...p,
                discontinued: targetDiscontinued,
                status: targetStatus,
              }
            : p
        )
      );

      // Update in selectedProduct if modal open
      setSelectedProduct((prev) =>
        prev && prev.id === product.id
          ? {
              ...prev,
              discontinued: targetDiscontinued,
              status: targetStatus,
            }
          : prev
      );

      // Update status counts
      setStatusCounts((prev) => ({
        ...prev,
        active: isCurrentlyDiscontinued ? prev.active + 1 : Math.max(0, prev.active - 1),
        disabled: isCurrentlyDiscontinued ? Math.max(0, prev.disabled - 1) : prev.disabled + 1,
      }));
    } catch (err) {
      console.error('Failed to update product status:', err);
      setActionMessage({
        type: 'error',
        text: err?.message || 'Failed to update product status',
      });
      setTimeout(() => setActionMessage(null), 5000);
    } finally {
      setTogglingProductId(null);
    }
  };

  const handleUpdateProductGender = async (product, targetGender) => {
    if (!product || !product.id || !targetGender) return;
    const currentGender = (product.gender || '').toLowerCase();
    const normalizedTarget = targetGender.toLowerCase();

    if (normalizedTarget === 'none') {
      if (!currentGender || currentGender === 'none') return;
    } else if (currentGender === normalizedTarget) {
      return;
    }

    setUpdatingGenderProductId(product.id);
    try {
      await adminApi.updateProductGender(product.id, targetGender);

      const newGenderValue = normalizedTarget === 'none' ? null : targetGender;

      // Update in products list
      setProducts((prev) =>
        prev.map((p) =>
          p.id === product.id
            ? { ...p, gender: newGenderValue }
            : p
        )
      );

      // Update in selectedProduct if modal open
      setSelectedProduct((prev) =>
        prev && prev.id === product.id
          ? { ...prev, gender: newGenderValue }
          : prev
      );

      // Update gender counts
      setGenderCounts((prev) => {
        const next = { ...prev };
        const oldKey = (!currentGender || currentGender === 'none') ? 'none' : currentGender;
        const newKey = normalizedTarget === 'none' ? 'none' : normalizedTarget;

        if (next[oldKey] !== undefined) {
          next[oldKey] = Math.max(0, next[oldKey] - 1);
        }
        next[newKey] = (next[newKey] || 0) + 1;
        return next;
      });

      // Ensure targetGender is in genders list if not 'none'
      if (normalizedTarget !== 'none') {
        setGenders((prev) => {
          if (!prev.includes(normalizedTarget)) {
            return [...prev, normalizedTarget];
          }
          return prev;
        });
      }
    } catch (err) {
      console.error('Failed to update product gender:', err);
      setActionMessage({
        type: 'error',
        text: err?.message || 'Failed to update product gender',
      });
      setTimeout(() => setActionMessage(null), 4000);
    } finally {
      setUpdatingGenderProductId(null);
    }
  };

  const formatPrice = (price) => {
    if (price == null || price === '') return null;
    const num = Number(price);
    if (isNaN(num)) return price;
    return `₹${num.toLocaleString('en-IN')}`;
  };

  const getDiscountPercent = (price, comparePrice) => {
    const p = Number(price);
    const cp = Number(comparePrice);
    if (!isNaN(p) && !isNaN(cp) && cp > p && p > 0) {
      const diff = Math.round(((cp - p) / cp) * 100);
      return diff > 0 ? `${diff}% OFF` : null;
    }
    return null;
  };

  const brandName = brand?.brand_name || brand?.title || 'Brand Gallery';
  const storefrontDomain = brand?.storefront_domain || brand?.storefront_url || '';
  const currentNote = (
    brand?.notes ||
    brand?.note ||
    brand?.custom_fields?.notes ||
    brand?.custom_fields?.note ||
    ''
  ).trim();

  return (
    <div className="brand-gallery-container">
      <TopMenu pageTitle={brandName} email={email} onLogout={onLogout} />

      <div className="brand-gallery-content">
        {/* Header */}
        <div className="brand-gallery-header">
          <div className="brand-gallery-header-left">
            <Link to="/brands" className="brand-gallery-back-btn" title="Back to Brands List">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
              <span>Back to Brands</span>
            </Link>

            <div className="brand-gallery-title-wrap">
              <h1 className="brand-gallery-title">
                {brandName}
                {brand?.partnership && <span className="brand-gallery-badge partner">⚡ Partner</span>}
                {brand?.is_active === false && <span className="brand-gallery-badge disabled">Disabled</span>}
              </h1>
              <div className="brand-gallery-meta">
                {storefrontDomain && (
                  <a
                    href={`https://${storefrontDomain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="brand-gallery-link"
                  >
                    <span>{storefrontDomain}</span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                      <polyline points="15 3 21 3 21 9" />
                      <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                  </a>
                )}
                <span>•</span>
                <span>{totalCount} Total Products</span>
              </div>
            </div>
          </div>
        </div>

        {/* Informational Banner if Brand is Disabled */}
        {brand?.is_active === false && (
          <div className="brand-gallery-archived-banner">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <div className="brand-gallery-archived-banner-text">
              <strong>Brand is currently Disabled.</strong> Showing archived products from <code>products_archived</code>. (Products cannot be activated individually while the brand is disabled).
            </div>
          </div>
        )}

        {/* Controls Bar: Gender Filter + Status Filter + Brand Note */}
        <div className="brand-gallery-controls-bar">
          {/* Filters Group (Gender + Status) */}
          <div className="brand-gallery-filters-group">
            {/* Gender Filter */}
            <div className="brand-gallery-filters-section">
              <div className="brand-gallery-control-label">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                </svg>
                Gender Filter
              </div>
              <div className="brand-gender-pills">
                <button
                  type="button"
                  className={`brand-gender-pill ${activeGender === 'all' ? 'active' : ''}`}
                  onClick={() => handleGenderChange('all')}
                >
                  All
                  <span className="brand-gender-count">
                    {Object.values(genderCounts).reduce((a, b) => a + b, 0) || totalCount}
                  </span>
                </button>

                {['women', 'men', ...genders.filter((g) => !['women', 'men', 'none'].includes(g))].map((g) => {
                  const count = genderCounts[g] || 0;
                  if (count === 0 && !genders.includes(g)) return null;
                  return (
                    <button
                      key={g}
                      type="button"
                      className={`brand-gender-pill ${activeGender === g ? 'active' : ''}`}
                      onClick={() => handleGenderChange(g)}
                    >
                      {g.charAt(0).toUpperCase() + g.slice(1)}
                      <span className="brand-gender-count">{count}</span>
                    </button>
                  );
                })}

                <button
                  type="button"
                  className={`brand-gender-pill ${activeGender === 'none' ? 'active' : ''}`}
                  onClick={() => handleGenderChange('none')}
                >
                  None
                  <span className="brand-gender-count">{genderCounts.none || 0}</span>
                </button>
              </div>
            </div>

            {/* Active / Disabled / Archived Status Filter */}
            <div className="brand-gallery-filters-section">
              <div className="brand-gallery-control-label">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                Status Filter
              </div>
              <div className="brand-gender-pills brand-status-pills">
                {brand?.is_active === false ? (
                  <button
                    type="button"
                    className="brand-gender-pill brand-status-pill-archived active"
                    onClick={() => handleStatusChange('all')}
                  >
                    Archived (products_archive)
                    <span className="brand-gender-count">
                      {totalCount}
                    </span>
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      className={`brand-gender-pill ${activeStatus === 'all' ? 'active' : ''}`}
                      onClick={() => handleStatusChange('all')}
                    >
                      All
                      <span className="brand-gender-count">
                        {statusCounts.all || (statusCounts.active + statusCounts.disabled) || totalCount}
                      </span>
                    </button>
                    <button
                      type="button"
                      className={`brand-gender-pill brand-status-pill-active ${activeStatus === 'active' ? 'active' : ''}`}
                      onClick={() => handleStatusChange('active')}
                    >
                      Active
                      <span className="brand-gender-count">{statusCounts.active || 0}</span>
                    </button>
                    <button
                      type="button"
                      className={`brand-gender-pill brand-status-pill-disabled ${activeStatus === 'disabled' ? 'active' : ''}`}
                      onClick={() => handleStatusChange('disabled')}
                    >
                      Disabled
                      <span className="brand-gender-count">{statusCounts.disabled || 0}</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Brand Note Section next to Gender Field */}
          <div className="brand-gallery-note-section">
            <div className="brand-gallery-control-label">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </svg>
              Brand Note
              {noteSuccess && <span style={{ color: '#16a34a', fontSize: '11px', fontWeight: 'bold' }}>✓ Saved!</span>}
            </div>

            <div className="brand-note-box">
              {isEditingNote ? (
                <div className="brand-note-edit-form">
                  <textarea
                    className="brand-note-textarea"
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Enter notes about this brand (e.g. style, sizing notes, contacts)..."
                    rows={3}
                    autoFocus
                  />
                  <div className="brand-note-actions">
                    <button
                      type="button"
                      className="brand-note-cancel-btn"
                      onClick={() => {
                        setNoteText(currentNote);
                        setIsEditingNote(false);
                      }}
                      disabled={savingNote}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="brand-note-save-btn"
                      onClick={handleSaveNote}
                      disabled={savingNote}
                    >
                      {savingNote ? 'Saving...' : 'Save Note'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="brand-note-display">
                  {currentNote ? (
                    <p className="brand-note-text">{currentNote}</p>
                  ) : (
                    <p className="brand-note-empty">No note assigned to this brand yet.</p>
                  )}
                  <button
                    type="button"
                    className="brand-note-edit-btn"
                    onClick={() => {
                      setNoteText(currentNote);
                      setIsEditingNote(true);
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                    {currentNote ? 'Edit' : 'Add Note'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Subbar: Search and items count */}
        <div className="brand-gallery-subbar">
          <input
            type="text"
            className="brand-gallery-search-input"
            placeholder="Search products by title..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <span className="brand-gallery-count-text">
            Showing {products.length} of {totalCount} products
          </span>
        </div>

        {/* Action / Toast Notification */}
        {actionMessage && (
          <div
            style={{
              padding: '12px 18px',
              borderRadius: '8px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: actionMessage.type === 'error' ? '#fee2e2' : '#f0fdf4',
              color: actionMessage.type === 'error' ? '#991b1b' : '#166534',
              border: `1px solid ${actionMessage.type === 'error' ? '#fecaca' : '#bbf7d0'}`,
              fontWeight: 500,
              fontSize: '14px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              animation: 'fadeIn 0.2s ease',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {actionMessage.type === 'error' ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              )}
              <span>{actionMessage.text}</span>
            </div>
            <button
              type="button"
              onClick={() => setActionMessage(null)}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: 'inherit',
                fontSize: '16px',
                padding: '2px 6px',
                lineHeight: 1,
              }}
            >
              ✕
            </button>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div style={{ padding: '12px 16px', background: '#fee2e2', color: '#b91c1c', borderRadius: '8px', marginBottom: '16px' }}>
            {error}
          </div>
        )}

        {/* Main Content Area */}
        {loading ? (
          <div className="brand-gallery-loading">
            <div className="brand-gallery-spinner" />
            <p>Loading {brandName} products...</p>
          </div>
        ) : products.length === 0 ? (
          <div className="brand-gallery-empty">
            <h3>No products found</h3>
            <p>
              {debouncedQuery
                ? `No products matching "${debouncedQuery}".`
                : activeGender === 'none'
                ? 'No untagged products found.'
                : activeGender !== 'all'
                ? `No products found for gender "${activeGender}".`
                : 'No products are currently available for this brand in the database.'}
            </p>
          </div>
        ) : (
          <>
            {/* 6 Products per Row Grid */}
            <div className="brand-products-grid">
              {products.map((p, idx) => {
                const mainImg = p.top_image || p.images?.[0]?.url || '';
                const imgCount = p.images?.length || 0;
                const formattedPrice = formatPrice(p.price);
                const formattedComparePrice = formatPrice(p.compare_at_price);
                const discountPercent = getDiscountPercent(p.price, p.compare_at_price);
                const isTopImage = idx < 12; // Eagerly load top visible rows
                const isArchived = Boolean(p.is_archived || p.archived || brand?.is_active === false);
                const isDiscontinued = isArchived || Boolean(
                  p.discontinued ||
                  String(p.status || '').toLowerCase() === 'inactive' ||
                  String(p.status || '').toLowerCase() === 'discontinued'
                );
                const isTogglingThis = togglingProductId === p.id;

                return (
                  <div
                    key={p.id || p.handle || idx}
                    className={`brand-product-card ${isArchived ? 'is-archived is-disabled' : isDiscontinued ? 'is-disabled' : ''}`}
                  >
                    {/* Product Image (Click to open modal gallery) */}
                    <div
                      className="brand-product-image-container"
                      onClick={() => openModal(p, 0)}
                      title="Click to view all images"
                    >
                      {mainImg ? (
                        <img
                          src={mainImg}
                          alt={p.title}
                          className="brand-product-image"
                          loading={isTopImage ? 'eager' : 'lazy'}
                          fetchPriority={isTopImage ? 'high' : 'auto'}
                          decoding="async"
                        />
                      ) : (
                        <div className="brand-product-no-image">
                          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                            <circle cx="8.5" cy="8.5" r="1.5" />
                            <polyline points="21 15 16 10 5 21" />
                          </svg>
                          <span>No image</span>
                        </div>
                      )}

                      {/* Status / Disabled / Archived Badge */}
                      {isArchived ? (
                        <div className="brand-product-status-badge archived" title="Product is archived in products_archived">
                          Archived
                        </div>
                      ) : isDiscontinued ? (
                        <div className="brand-product-status-badge disabled" title="Product is disabled / discontinued">
                          Disabled
                        </div>
                      ) : null}

                      {/* Image count badge */}
                      {imgCount > 1 && (
                        <div className="brand-product-image-badge" title={`${imgCount} images available`}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                            <circle cx="8.5" cy="8.5" r="1.5" />
                            <polyline points="21 15 16 10 5 21" />
                          </svg>
                          <span>{imgCount}</span>
                        </div>
                      )}

                      {/* Gender badge */}
                      {p.gender && (
                        <div className="brand-product-gender-badge">
                          {p.gender}
                        </div>
                      )}
                    </div>

                    {/* Product Info with Price below Image */}
                    <div className="brand-product-info">
                      <div>
                        {/* Price below the image */}
                        <div className="brand-product-price-row">
                          {formattedPrice ? (
                            <span className="brand-product-price">{formattedPrice}</span>
                          ) : (
                            <span className="brand-product-price" style={{ color: '#94a3b8' }}>—</span>
                          )}
                          {formattedComparePrice && formattedComparePrice !== formattedPrice && (
                            <span className="brand-product-compare-price">{formattedComparePrice}</span>
                          )}
                          {discountPercent && (
                            <span className="brand-product-discount">{discountPercent}</span>
                          )}
                        </div>

                        {/* Title */}
                        <h4 className="brand-product-title" title={p.title}>
                          {p.title}
                        </h4>
                      </div>

                      {/* Card Action: Discontinue / Mark Active Product / Archived Label */}
                      <div className="brand-product-card-actions">
                        <span className="brand-product-seq-num" title={`Product #${idx + 1} of ${totalCount}`}>
                          #{idx + 1}
                        </span>
                        {isArchived ? (
                          <span className="brand-product-archived-tag" title="Archived products cannot be activated while the brand is disabled">
                            Archived
                          </span>
                        ) : (
                          <button
                            type="button"
                            className={`brand-product-toggle-btn ${isDiscontinued ? 'enable' : 'disable'}`}
                            onClick={(e) => handleToggleProductStatus(p, e)}
                            disabled={isTogglingThis}
                            title={isDiscontinued ? 'Mark this product as active' : 'Discontinue this product (marks discontinued: true and status: inactive)'}
                          >
                            {isTogglingThis ? (
                              <span className="brand-btn-inline-loader">Updating...</span>
                            ) : isDiscontinued ? (
                              <>
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                  <path d="M20 6L9 17l-5-5" />
                                </svg>
                                <span>Mark Active</span>
                              </>
                            ) : (
                              <>
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <circle cx="12" cy="12" r="10" />
                                  <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                                </svg>
                                <span>Discontinue</span>
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Infinite Scroll Sentinel / Loading Spinner */}
            {hasMore && (
              <div ref={sentinelRef} className="brand-gallery-scroll-loader">
                <div className="brand-gallery-spinner small" />
                <span>Loading more products...</span>
              </div>
            )}

            {!hasMore && products.length > 0 && (
              <div className="brand-gallery-scroll-end">
                All {totalCount} products loaded
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal Gallery of all product images */}
      {selectedProduct && (
        <div className="gallery-modal-overlay" onClick={closeModal}>
          <div className="gallery-modal-content" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="gallery-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <h3 className="gallery-modal-title">{selectedProduct.title}</h3>
                {Boolean(selectedProduct.is_archived || selectedProduct.archived || brand?.is_active === false) ? (
                  <span className="brand-gallery-badge archived">Archived (products_archive)</span>
                ) : Boolean(
                  selectedProduct.discontinued ||
                  String(selectedProduct.status || '').toLowerCase() === 'inactive' ||
                  String(selectedProduct.status || '').toLowerCase() === 'discontinued'
                ) ? (
                  <span className="brand-gallery-badge disabled">Disabled</span>
                ) : (
                  <span className="brand-gallery-badge partner" style={{ background: '#f0fdf4', color: '#16a34a', borderColor: '#bbf7d0' }}>Active</span>
                )}
              </div>
              <button
                type="button"
                className="gallery-modal-close-btn"
                onClick={closeModal}
                title="Close (Esc)"
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div className="gallery-modal-body">
              {/* Main Image View */}
              <div className="gallery-modal-main-image-wrap">
                {(selectedProduct.images?.[activeImageIndex]?.url || selectedProduct.top_image) ? (
                  <img
                    src={selectedProduct.images?.[activeImageIndex]?.url || selectedProduct.top_image}
                    alt={selectedProduct.title}
                    className="gallery-modal-main-image"
                  />
                ) : (
                  <div style={{ color: '#94a3b8' }}>No image preview</div>
                )}

                {/* Left/Right navigation arrows */}
                {selectedProduct.images?.length > 1 && (
                  <>
                    <button
                      type="button"
                      className="gallery-modal-nav-btn prev"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePrevImage();
                      }}
                      title="Previous Image (←)"
                    >
                      ‹
                    </button>
                    <button
                      type="button"
                      className="gallery-modal-nav-btn next"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNextImage();
                      }}
                      title="Next Image (→)"
                    >
                      ›
                    </button>
                  </>
                )}

                {/* Image counter */}
                {selectedProduct.images?.length > 0 && (
                  <div className="gallery-modal-counter">
                    {activeImageIndex + 1} / {selectedProduct.images.length}
                  </div>
                )}
              </div>
            </div>

            {/* Thumbnails reel */}
            {selectedProduct.images?.length > 1 && (
              <div className="gallery-modal-thumbnails-reel">
                {selectedProduct.images.map((img, idx) => (
                  <div
                    key={img.id || img.url || idx}
                    className={`gallery-modal-thumb ${idx === activeImageIndex ? 'active' : ''}`}
                    onClick={() => setActiveImageIndex(idx)}
                  >
                    <img src={img.url} alt={`Thumbnail ${idx + 1}`} />
                  </div>
                ))}
              </div>
            )}

            {/* Footer */}
            <div className="gallery-modal-footer">
              <div className="gallery-modal-footer-price">
                {selectedProduct.price != null && (
                  <span className="brand-product-price" style={{ fontSize: '18px' }}>
                    {formatPrice(selectedProduct.price)}
                  </span>
                )}
                {selectedProduct.compare_at_price != null && (
                  <span className="brand-product-compare-price">
                    {formatPrice(selectedProduct.compare_at_price)}
                  </span>
                )}
                {getDiscountPercent(selectedProduct.price, selectedProduct.compare_at_price) && (
                  <span className="brand-product-discount" style={{ fontSize: '13px' }}>
                    {getDiscountPercent(selectedProduct.price, selectedProduct.compare_at_price)}
                  </span>
                )}
                {/* Gender Toggle Pills */}
                <div className="gallery-modal-gender-switcher">
                  <span className="gallery-modal-gender-label">Gender:</span>
                  <div className="gallery-modal-gender-pills">
                    {['women', 'men'].map((g) => {
                      const cur = (selectedProduct.gender || '').toLowerCase();
                      const isCurrent = cur === g ||
                        (cur === 'female' && g === 'women') ||
                        (cur === 'male' && g === 'men');
                      const isUpdating = updatingGenderProductId === selectedProduct.id;
                      return (
                        <button
                          key={g}
                          type="button"
                          className={`gallery-modal-gender-pill ${isCurrent ? 'active' : ''}`}
                          onClick={() => handleUpdateProductGender(selectedProduct, g)}
                          disabled={isUpdating}
                          title={`Set gender to ${g === 'women' ? 'Women' : 'Men'}`}
                        >
                          {g === 'women' ? 'Women' : 'Men'}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {currentProductIndex >= 0 && (
                  <span className="gallery-modal-product-index-badge">
                    Product {currentProductIndex + 1} of {totalCount || products.length}
                  </span>
                )}
              </div>

              <div className="gallery-modal-footer-actions">
                {/* Discontinue / Mark Active button OR Archived read-only indicator */}
                {Boolean(selectedProduct.is_archived || selectedProduct.archived || brand?.is_active === false) ? (
                  <div className="gallery-modal-archived-pill" title="Brand is disabled; products in products_archive cannot be made active individually">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 8v13H3V8" />
                      <path d="M1 3h22v5H1z" />
                      <path d="M10 12h4" />
                    </svg>
                    <span>Archived Product</span>
                  </div>
                ) : (
                  <button
                    type="button"
                    className={`gallery-modal-status-btn ${
                      Boolean(
                        selectedProduct.discontinued ||
                        String(selectedProduct.status || '').toLowerCase() === 'inactive' ||
                        String(selectedProduct.status || '').toLowerCase() === 'discontinued'
                      )
                        ? 'enable'
                        : 'disable'
                    }`}
                    onClick={() => handleToggleProductStatus(selectedProduct)}
                    disabled={togglingProductId === selectedProduct.id}
                    title={
                      Boolean(
                        selectedProduct.discontinued ||
                        String(selectedProduct.status || '').toLowerCase() === 'inactive' ||
                        String(selectedProduct.status || '').toLowerCase() === 'discontinued'
                      )
                        ? 'Mark this product as active'
                        : 'Discontinue this product (marks discontinued: true and status: inactive)'
                    }
                  >
                    {togglingProductId === selectedProduct.id ? (
                      'Updating...'
                    ) : Boolean(
                        selectedProduct.discontinued ||
                        String(selectedProduct.status || '').toLowerCase() === 'inactive' ||
                        String(selectedProduct.status || '').toLowerCase() === 'discontinued'
                      ) ? (
                      <>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                        <span>Mark Active</span>
                      </>
                    ) : (
                      <>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                        </svg>
                        <span>Discontinue Product</span>
                      </>
                    )}
                  </button>
                )}

                {/* Product Navigation: Prev Product */}
                <button
                  type="button"
                  className="gallery-modal-prod-nav-btn prev"
                  onClick={handlePrevProduct}
                  disabled={currentProductIndex <= 0}
                  title="Switch to Previous Product (Shift + ← or [)"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                  <span>Prev Product</span>
                </button>

                {/* Product Navigation: Next Product */}
                <button
                  type="button"
                  className="gallery-modal-prod-nav-btn next"
                  onClick={handleNextProduct}
                  disabled={currentProductIndex >= products.length - 1 && !hasMore}
                  title="Switch to Next Product (Shift + → or ])"
                >
                  <span>Next Product</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>

                {/* View Product in Store */}
                {selectedProduct.url && (
                  <a
                    href={selectedProduct.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="gallery-modal-store-btn"
                    title="View Product in Store"
                  >
                    <span>View Product</span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                      <polyline points="15 3 21 3 21 9" />
                      <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Back to top floating button */}
      {showBackToTop && (
        <button
          type="button"
          className="brand-gallery-back-to-top"
          onClick={scrollToTop}
          aria-label="Back to top"
          title="Back to top"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="18 15 12 9 6 15" />
          </svg>
        </button>
      )}
    </div>
  );
}

export default BrandGallery;
